import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
  forwardRef,
} from 'react';
import * as Sentry from '@sentry/browser';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Collection } from 'firestorter';

import ChatChannelContext from '../../context/ChatChannelContext';
import ChatContext from '../../context/ChatContext';
import ChatMessage from '../../Model/ChatMessage';
import useUserDoc from '../../../CoachTools/hooks/useUserDoc';
import {
  areDatesTheSameDay,
  formatDateForChat,
  formatTimeForChatMessage,
} from '../../utils/time';
import { getMessageVariant } from '../MessagePreview/variants';
import EmptyView from '../EmptyView';
import MessagePreview from '../MessagePreview';
import ThreadModal from '../ThreadModal';
import LoadingPage from '../LoadingPage';
import CollectionName from '../../../utils/collections';

import {
  Wrapper,
  Container,
  MessageListEnd,
  DateSeparator,
  ScrollToBottomButton,
  MessageAnchor,
  InternalIntervention,
  InternalInterventionTitle,
  InternalInterventionTime,
} from './styles';
import texts from './texts.json';
import mergeTimelineItems from './utils';

const HIGHLIGHT_DURATION_MS = 3000;
const MAX_FOCUS_PAGES = 20;
const PAUSE_INTERVENTION_STATUS_LABELS = {
  pending: 'Pending',
  paused_manually: 'Paused manually',
  retained: 'Retained',
};

const ChannelMessageList = forwardRef((_, messageListEndRef) => {
  const {
    chatUser,
    authUser,
    showInternalText,
    sessionStore,
    focusMessageId,
    clearFocusMessage,
  } = useContext(ChatContext);
  const {
    messages,
    visibleMessages,
    isReady,
    isLoadingMessages,
    loadMoreMessages,
    hasMoreMessages,
    activeChannel,
    isVideoReady,
    setIsVideoReady,
    threadNoticeRootId,
    setThreadNoticeRootId,
  } = useContext(ChatChannelContext);
  const [noticeRootMessage, setNoticeRootMessage] = useState(null);
  const messagesContainerRef = useRef(null);
  const messageRefs = useRef({});
  const focusAttemptsRef = useRef(0);
  const [isUserScrollingUp, setIsUserScrollingUp] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState('');

  // Fetch the client's User doc so coaches can highlight chat text
  // and append it to the client's notes via the "Add to Notes" menu.
  const { userDoc: clientUserDoc } = useUserDoc(activeChannel?.userId, { viewAsCoachId: activeChannel?.coachId });
  const activeChannelId = activeChannel?.id;
  const pauseInterventionCollection = useMemo(() => (
    activeChannelId
      ? new Collection(CollectionName.PAUSE_INTERVENTION, {
        query: (ref) => ref.where('chatChannelId', '==', activeChannelId),
      })
      : null
  ), [activeChannelId]);
  const pauseInterventions = pauseInterventionCollection
    ? pauseInterventionCollection.docs.map((doc) => ({
      ...doc.data,
      id: `pause-intervention-${doc.id}`,
      isPauseIntervention: true,
      createdAt: doc.data.signaledAt?.toDate(),
      resolvedAt: doc.data.resolvedAt?.toDate(),
    }))
    : [];

  /*
    The pill in the header points at a root that scrolled out of the loaded window, so it is not in
    `messages` — fetch its own document to open the thread on.
  */
  useEffect(() => {
    if (!threadNoticeRootId) {
      setNoticeRootMessage(null);
      return undefined;
    }
    const rootDoc = new ChatMessage(threadNoticeRootId);
    let cancelled = false;
    rootDoc.init().then(() => {
      if (!cancelled) {
        setNoticeRootMessage(rootDoc);
      }
    }).catch((error) => {
      Sentry.captureException(error, {
        extra: {
          errorMessage: 'Failed to load thread root message',
          rootMessageId: threadNoticeRootId,
        },
      });
      if (!cancelled) {
        setThreadNoticeRootId(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [setThreadNoticeRootId, threadNoticeRootId]);

  const renderThreadReply = (threadMessage) => {
    const isMirroredSender = !!threadMessage.actualSenderName;
    const memberInfo = activeChannel?.memberData?.[threadMessage.userId] || {};

    return (
      <MessagePreview
        key={threadMessage.id}
        message={threadMessage}
        variant={getMessageVariant(threadMessage, chatUser)}
        image={isMirroredSender ? threadMessage.actualSenderAvatarUrl : (memberInfo.image || '')}
        name={isMirroredSender ? threadMessage.actualSenderName : (memberInfo.name || '')}
        showSenderLabel={isMirroredSender}
        showInternalText={showInternalText}
        clientUserDoc={clientUserDoc}
        viewerId={authUser?.id}
        isCoachAssistant={!!sessionStore?.isCoachAssistant}
        showACSenderNames={!!authUser?.showACSenderNames}
      />
    );
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (isReady && !isLoadingMessages && !isUserScrollingUp && !focusMessageId && container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [
    isReady,
    messages,
    isLoadingMessages,
    isUserScrollingUp,
    messagesContainerRef,
    focusMessageId,
  ]);

  useEffect(() => {
    if (!focusMessageId || !isReady || isLoadingMessages) {
      return undefined;
    }

    const targetNode = messageRefs.current[focusMessageId];

    if (targetNode) {
      focusAttemptsRef.current = 0;
      // Arm the highlight-clear timeout only on first detection. `messages` is a dep (needed so the
      // effect re-runs and re-finds the node after progressive paging), so without this guard every
      // incoming message would reset the timeout and keep the highlight alive past HIGHLIGHT_DURATION_MS.
      if (highlightedMessageId !== focusMessageId) {
        targetNode.scrollIntoView({ block: 'center' });
        setHighlightedMessageId(focusMessageId);
        const timeoutId = setTimeout(() => {
          setHighlightedMessageId('');
          clearFocusMessage();
        }, HIGHLIGHT_DURATION_MS);
        return () => clearTimeout(timeoutId);
      }
      return undefined;
    }

    if (hasMoreMessages && focusAttemptsRef.current < MAX_FOCUS_PAGES) {
      focusAttemptsRef.current += 1;
      loadMoreMessages();
    } else {
      // Message not reachable (very old or not in this channel) — stop trying.
      focusAttemptsRef.current = 0;
      clearFocusMessage();
    }

    return undefined;
  }, [
    focusMessageId,
    isReady,
    isLoadingMessages,
    messages,
    hasMoreMessages,
    loadMoreMessages,
    clearFocusMessage,
    highlightedMessageId,
  ]);

  useEffect(() => {
    if (isVideoReady && !isUserScrollingUp) {
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (!isUserScrollingUp && container) {
          container.scrollTop = container.scrollHeight;
        }
        setIsVideoReady(false);
      }, 100);
    }
  }, [isUserScrollingUp, isVideoReady, setIsVideoReady]);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 100) {
      setIsUserScrollingUp(false);
    } else {
      setIsUserScrollingUp(true);
    }

    if (container.scrollTop < 200 && !isLoadingMessages && isReady) {
      if (hasMoreMessages) {
        loadMoreMessages();
      }
    }
  };

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  const orderedMessages = mergeTimelineItems(visibleMessages, pauseInterventions);

  if (isReady && !isLoadingMessages && !hasMoreMessages && orderedMessages.length === 0) {
    return <EmptyView content={texts.noMessages} />;
  }

  return (
    <Wrapper>
      <Container
        ref={messagesContainerRef}
        onScroll={handleScroll}
        data-testid="chat-message-list"
      >
        {!isReady && isLoadingMessages && <LoadingPage />}
        {orderedMessages.map((message, index) => {
          const previousMessage = orderedMessages[index - 1];
          const nextMessage = orderedMessages[index + 1];
          const startsNewDay = !!message.createdAt
            && (index === 0 || !areDatesTheSameDay(previousMessage.createdAt, message.createdAt));

          if (message.isPauseIntervention) {
            return (
              <Fragment key={message.id}>
                {startsNewDay && (
                  <DateSeparator>{formatDateForChat(message.createdAt)}</DateSeparator>
                )}
                <InternalIntervention>
                  <InternalInterventionTitle>
                    {`Internal • Pause intervention • ${PAUSE_INTERVENTION_STATUS_LABELS[message.status]}`}
                  </InternalInterventionTitle>
                  <div>{message.commentText}</div>
                  <InternalInterventionTime>
                    {message.resolvedAt && (
                      <div>
                        {`Resolved ${formatDateForChat(message.resolvedAt)} at ${
                          formatTimeForChatMessage(message.resolvedAt)
                        }`}
                      </div>
                    )}
                    <div>{`Requested at ${formatTimeForChatMessage(message.createdAt)}`}</div>
                  </InternalInterventionTime>
                </InternalIntervention>
              </Fragment>
            );
          }

          const memberInfo = activeChannel.memberData[message.userId] || {};
          // @Avida-mirrored messages (Admin Coaching Channel) snapshot the real Slack sender onto
          // themselves; everything else falls back to the channel-member lookup (Avida brand, or
          // coach/client identity).
          const isMirroredSender = !!message.actualSenderName;
          const displayName = isMirroredSender ? message.actualSenderName : (memberInfo.name || '');
          const displayImage = isMirroredSender ? message.actualSenderAvatarUrl : (memberInfo.image || '');
          // Consecutive messages from the same mirrored sender form a run: the name goes inside the
          // first bubble, the avatar (and so the bubble tail) on the last one ("Telegram style").
          // A date separator breaks the run too, otherwise the group visually spans it: the last
          // bubble above the separator would lose its avatar and the first one below its name.
          // Non-mirrored messages are never grouped — they always show their avatar (unchanged).
          const nextIsSameDay = !!nextMessage && areDatesTheSameDay(message.createdAt, nextMessage.createdAt);
          const isRunStart = index === 0
            || startsNewDay
            || previousMessage.actualSenderId !== message.actualSenderId;
          const isRunEnd = !nextIsSameDay || nextMessage.actualSenderId !== message.actualSenderId;
          const showSenderLabel = isMirroredSender && isRunStart;
          const showAvatar = !isMirroredSender || isRunEnd;

          return (
            <Fragment key={message.id}>
              {startsNewDay && (
                <DateSeparator>{formatDateForChat(message.createdAt)}</DateSeparator>
              )}
              <MessageAnchor
                ref={(el) => { messageRefs.current[message.id] = el; }}
                $isHighlighted={highlightedMessageId === message.id}
              >
                <MessagePreview
                  message={message}
                  variant={getMessageVariant(message, chatUser)}
                  image={displayImage}
                  name={displayName}
                  showSenderLabel={showSenderLabel}
                  showAvatar={showAvatar}
                  showInternalText={showInternalText}
                  clientUserDoc={clientUserDoc}
                  viewerId={authUser?.id}
                  isCoachAssistant={!!sessionStore?.isCoachAssistant}
                  showACSenderNames={!!authUser?.showACSenderNames}
                />
              </MessageAnchor>
            </Fragment>
          );
        })}
        <MessageListEnd ref={messageListEndRef} />
      </Container>
      {!!noticeRootMessage && (
        <ThreadModal
          rootMessage={noticeRootMessage}
          isOpen
          onClose={() => setThreadNoticeRootId(null)}
          renderMessage={renderThreadReply}
        />
      )}
      {isUserScrollingUp && (
        <ScrollToBottomButton onClick={scrollToBottom}>
          <KeyboardArrowDownIcon />
        </ScrollToBottomButton>
      )}
    </Wrapper>
  );
});

export default compose(
  observer,
)(ChannelMessageList);
